<template>
  <section>
    <!-- Part Type Radio (Bikes/Accesories) -->
    <form-group
      class="my-2"
      type="radio"
      qaid="warranty-claim-line-issue-when"
      :multi-items="partTypeOptions"
      :value="claimLine.productType"
      :name="inputName('part-type')"
      @change="handlePartTypeChange"
    >
      <template v-slot:label>
        <legend
          class="form-fieldset__legend text-lg font-bold"
          qaid="warranty-claim-line-details-type-label"
          v-html="bp('productType')"
        />
      </template>
    </form-group>

    <hr class="rule my-4" />

    <section-header size="sm" class="mb-3">
      <heading :level="4" size="sm" qaid="warranty-claim-line-details-sub-header" v-html="bp('productDetails')" />
    </section-header>

    <!-- Serial Number -->
    <div class="grid-x grid-margin-x">
      <div class="cell large-6">
        <div v-if="claimLine.productType === 'Bikes'">
          <div
            class="form-group"
            :class="{'has-error': !!errors.first(inputName('serialNumber')) || showSerialWarning}"
          >
            <p class="mb-1" v-html="bp('enterSerialNumber')" />
            <label
              for="serial-number"
              class="form-label"
              qaid="warranty-claim-line-details-serial-label"
              v-html="bp('serialNumber')"
            />
            <div class="form-group--inline is-required mr-0">
              <input
                v-show="claimLine.productType === 'Bikes'"
                id="serial-number"
                :key="inputName('serialNumber')"
                v-model="claimLine.serialNumber"
                v-validate="{required: claimLine.productType === 'Bikes'}"
                type="text"
                class="form-control flex-grow"
                qaid="warranty-claim-line-details-serial"
                :data-vv-as="bp('serialNumber')"
                :name="inputName('serialNumber')"
                :maxlength="30"
                :placeholder="bp('enterSerialNumberPlaceholder')"
                @change="updateClaimLine"
              />
              <trek-button primary form-inline class="md:ml-1" @click="processSerial">
                <span v-html="bp('addProduct')"></span>
              </trek-button>
            </div>
            <div
              v-show="errors.first(inputName('serialNumber'))"
              class="form-feedback is-invalid"
              v-html="errors.first(inputName('serialNumber'))"
            />
            <div v-if="showSerialNotFound" class="my-2 text-warning" v-html="bp('submitSerialImage')" />
          </div>
          <!-- Serial Number Image -->
          <div class="my-2 text-center" v-html="bp('storeFinderLineText')" />
          <div class="form-group lg:w-3/4" :class="{'has-error': !!errors.first(inputName('serialImageValidator'))}">
            <label
              class="form-label"
              qaid="warranty-claim-line-details-serial-image-label"
              v-html="bp('serialImage')"
            />
            <file-upload
              :click-to-upload-msg="bp('uploadImage')"
              :upload-button="false"
              :input-id="`${claimIndex}_serialNumberImage`"
              :in-file-name="hasSerialImage ? claimLine.serialNumberImage.name : null"
              :help="null"
              :in-preset-image-file-url="hasSerialImage ? claimLine.serialNumberImage.url : null"
              qaid="warranty-claim-line-details-serial-image"
              :upload-on-input="true"
              :has-error="!!errors.first(inputName('serialImageValidator'))"
              :base-properties="baseProperties"
              @file-uploaded="setImage({property: 'serialNumberImage'}, $event)"
              @clear-file="clearImage('serialNumberImage')"
            ></file-upload>
            <input
              v-validate="requireImageSerialImage ? 'required' : ''"
              type="hidden"
              :value="claimLine.serialNumberImage ? claimLine.serialNumberImage.name : ''"
              :name="inputName('serialImageValidator')"
              :data-vv-as="bp('serialImage')"
            />
            <div
              v-show="errors.first(inputName('serialImageValidator'))"
              class="form-feedback is-invalid"
              v-html="errors.first(inputName('serialImageValidator'))"
            />
          </div>
        </div>
        <!-- Validate SKU Number -->
        <div v-if="claimLine.productType === 'PartsAndAccessories'">
          <div class="form-group" :class="{'has-error': !!errors.first(inputName('sku'))}">
            <p class="mb-1" v-html="bp('enterProductSKU')" />
            <label for="sku" class="form-label" qaid="warranty-claim-line-details-sku-label">
              {{ bp('enterProductSKUPlaceholder') }}
              <span class="text-red ml-25">
                *
                <span class="show-for-sr" v-html="bp('required')" />
              </span>
            </label>
            <div class="form-group--inline is-required mr-0">
              <input
                v-model="claimLine.sku"
                v-validate="'required'"
                type="text"
                :maxlength="25"
                class="form-control flex-grow"
                qaid="warranty-claim-line-details-sku"
                :data-vv-as="bp('productSKU')"
                :name="inputName('sku')"
                required
                @change="updateClaimLine"
              />
              <trek-button primary form-inline class="md:ml-1" @click="processSku">
                <span v-html="bp('addProduct')"></span>
              </trek-button>
            </div>
            <div
              v-show="errors.first(inputName('sku'))"
              class="form-feedback is-invalid"
              v-html="errors.first(inputName('sku'))"
            />
            <div v-if="showSkuNotFound" class="my-2 text-warning" v-html="bp('confirmSku')" />
          </div>
        </div>
      </div>

      <div class="cell large-6">
        <div class="grid-x grid-margin-x relative">
          <div
            v-if="!enableProductFields"
            class="warranty-claim-product-overlay"
            :class="{'warranty-claim-product-overlay--loading': confirmLoading}"
          >
            <p v-if="!confirmLoading" class="warranty-claim-product-overlay__label">
              <i class="md-24">note_add</i>
              <span
                class="inline-block ml-1"
                v-html="claimLine.productType === 'PartsAndAccessories' ? bp('productAppear') : bp('enterGetStarted')"
              />
            </p>
            <trek-loading v-else :message="bp('productLoading')" :is-loading="confirmLoading" />
          </div>
          <div class="cell large-9">
            <form-group
              v-model="claimLine.modelName"
              v-validate="'required'"
              type="text"
              qaid="warranty-claim-line-details-model"
              required
              :name="inputName('model')"
              :error="errors.first(inputName('model'))"
              :show-error-messages="true"
              :data-vv-as="bp('type')"
              :label="bp('type')"
              @change="updateClaimLine"
            />
          </div>
          <div class="cell large-3">
            <form-group
              v-model="claimLine.qty"
              v-validate="'required'"
              type="number"
              qaid="warranty-claim-line-details-qty"
              required
              :name="inputName('qty')"
              :error="errors.first(inputName('qty'))"
              :show-error-messages="true"
              :data-vv-as="bp('qty')"
              :label="bp('qty')"
              @change="updateClaimLine"
            />
          </div>
          <div v-if="claimLine.productType === 'Bikes'" class="cell large-6">
            <form-group
              v-model="claimLine.sku"
              v-validate="'required'"
              type="text"
              qaid="warranty-claim-line-details-sku"
              required
              :name="inputName('sku')"
              :error="errors.first(inputName('sku'))"
              :show-error-messages="true"
              :data-vv-as="bp('sku')"
              :label="bp('sku')"
              :maxlength="25"
              @change="updateClaimLine"
            />
          </div>
          <div v-if="claimLine.productType === 'Bikes'" class="cell large-6">
            <form-group
              v-model="claimLine.modelYear"
              v-validate="'required'"
              type="text"
              qaid="warranty-claim-line-details-year"
              required
              :name="inputName('modelYear')"
              :error="errors.first(inputName('modelYear'))"
              :show-error-messages="true"
              :data-vv-as="bp('year')"
              :label="bp('year')"
              @change="updateClaimLine"
            />
          </div>
          <div class="cell large-6">
            <form-group
              v-model="claimLine.color"
              v-validate="claimLine.productType === 'Bikes' ? 'required' : ''"
              type="text"
              qaid="warranty-claim-line-details-color"
              :name="inputName('color')"
              :error="errors.first(inputName('color'))"
              :show-error-messages="true"
              :data-vv-as="bp('color')"
              :label="bp('color')"
              required
              @change="updateClaimLine"
            />
          </div>
          <div class="cell large-6">
            <form-group
              v-model="claimLine.size"
              v-validate="claimLine.productType === 'Bikes' ? 'required' : ''"
              type="text"
              qaid="warranty-claim-line-details-size"
              :name="inputName('size')"
              :error="showSizeWarning ? sizeWarning : errors.first(inputName('size'))"
              :show-error-messages="true"
              :data-vv-as="bp('productSize')"
              :label="bp('productSize')"
              required
              @change="updateClaimLine"
            />
          </div>
          <div class="cell large-6">
            <div
              id="purchase-date-field"
              class="form-group"
              :class="{'has-error': !!errors.first(inputName('purchaseDate'))}"
            >
              <label class="font-medium flex" qaid="warranty-claim-line-details-purchase-label">
                <span v-html="bp('purchaseDate')" />
                <span class="text-red ml-25">
                  *
                  <span class="show-for-sr" v-html="bp('required')" />
                </span>
              </label>
              <date-picker
                id="purchase-date-input"
                ref="purchaseDatePicker"
                v-validate="'required'"
                qaid="warranty-claim-line-details-purchase"
                :allow-input="true"
                :name="inputName('purchaseDate')"
                :error="errors.first(inputName('purchaseDate'))"
                :data-vv-as="bp('purchaseDate')"
                :value="claimLine.purchasedDate"
                :max-date="new Date()"
                required
                @input="updatePurchaseDate"
              ></date-picker>
              <div
                v-show="errors.first(inputName('purchaseDate'))"
                class="form-feedback is-invalid"
                v-html="errors.first(inputName('purchaseDate'))"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import {mapActions, mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';
import FormGroup from '@/components/FormGroup';
import TrekLoading from '@/components/TrekLoading';
import SectionHeader from '@/components/SectionHeader';
import DatePicker from '@/components/DatePicker';
import Heading from '@/components/Heading';

export default {
  components: {TrekButton, DatePicker, FormGroup, TrekLoading, SectionHeader, Heading},
  inject: ['$validator'],

  props: {
    baseProperties: {
      type: Array,
      required: true,
    },
    claimLine: {
      type: Object,
      required: true,
    },
    claimIndex: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      confirmLoading: false,
      showSerialNotFound: false,
      showSkuNotFound: false,
      validator: null,
      serialValidated: false,
      loadedProductDataForSku: '',
    };
  },

  computed: {
    enableProductFields() {
      if (this.confirmLoading) {
        return false;
      }

      if (this.claimLine.productType === 'PartsAndAccessories') {
        return this.loadedProductDataForSku && this.loadedProductDataForSku === this.claimLine.sku;
      }

      return (this.serialValidated && !this.showSerialNotFound) || this.hasSerialImage;
    },

    hasSerialImage() {
      return get(this, 'claimLine.serialNumberImage.url.length', 0) > 0;
    },

    showSerialWarning() {
      return includes(this.claimLine.waitingCodeList, 'S');
    },

    showSizeWarning() {
      return includes(this.claimLine.waitingCodeList, 'F');
    },

    requireImageSerialImage() {
      const requiredByIssueType =
        this.claimLine.warrantyRequiredImagesList &&
        includes(this.claimLine.warrantyRequiredImagesList, 'EnumWarrantyRequiredImages003');

      return (this.claimLine.productType === 'Bikes' && !this.serialValidated) || requiredByIssueType;
    },

    sizeWarning() {
      return this.bp('sizeRequired').substring(
        this.bp('sizeRequired').indexOf('-') + 2,
        this.bp('sizeRequired').length
      );
    },

    partTypeOptions() {
      return [
        {
          value: 'Bikes',
          displayName: this.bp('bikes'),
        },
        {
          value: 'PartsAndAccessories',
          displayName: this.bp('partsAccessories'),
        },
      ];
    },

    ...mapState('warrantyClaims', ['csrf', 'validators']),
  },

  watch: {
    claimLine() {
      if (this.claimLine && this.claimLine.qty === 0) this.claimLine.qty = null;
    },
  },

  mounted() {
    this.validators.push(this.$validator);

    // Assuming that if the claim line has a serial already then it's verified (for validation purposes)
    if (this.claimLine.productType === 'Bikes' && this.claimLine.serialNumber && this.claimLine.serialNumber.length) {
      this.serialValidated = true;
    } else if (
      this.claimLine.productType === 'PartsAndAccessories' &&
      this.claimLine.sku &&
      this.claimLine.sku.length
    ) {
      this.serialValidated = true;
      this.loadedProductDataForSku = this.claimLine.sku;
    }
  },

  methods: {
    inputName(propertyName) {
      return `cl${this.claimIndex}_${propertyName}`;
    },

    processSerial() {
      this.clearWarningMessages();

      // Clear old fields
      this.claimLine.sku = '';
      this.claimLine.modelName = '';
      this.claimLine.modelYear = '';
      this.claimLine.size = '';
      this.claimLine.color = '';

      this.serialValidated = false;
      this.claimLine.warrantyRequiredImagesList = [];

      this.claimLine.issueTypeNameSelected = '';
      this.$forceUpdate();

      if (this.claimLine && this.claimLine.serialNumber) {
        this.confirmLoading = true;

        axios
          .get(`/claims/serial/${this.claimLine.serialNumber}/`)
          .then((response) => {
            if (!isEmpty(response.data.data)) {
              this.claimLine.sku = response.data.data.sku;
              this.claimLine.modelName = response.data.data.modelName;
              this.claimLine.modelYear = response.data.data.modelYear;
              this.claimLine.size = response.data.data.size;
              this.claimLine.color = response.data.data.color;

              this.$emit('issue-types-update', response.data.data.warrantyIssues);

              this.serialValidated = true;

              // Force update since we're modifying deeper attributes than what Vue is watching for
              this.$forceUpdate();
            }

            if (!isEmpty(response.data.meta.feedback.message)) {
              if (response.data.meta.feedback.message.basePropertyKey === 'warrantyClaims.B2B.unableLocateSerial') {
                this.showSerialNotFound = true;
                this.serialValidated = false;
              }
            }
          })
          .finally(() => {
            this.confirmLoading = false;
            this.autoSaveClaim({csrf: this.csrf});
          });
      }
    },

    processSku() {
      this.clearWarningMessages();

      // Clear old fields
      this.claimLine.modelName = '';
      this.claimLine.modelYear = '';
      this.claimLine.size = '';
      this.claimLine.color = '';

      this.serialValidated = false;
      this.claimLine.warrantyRequiredImagesList = [];
      this.loadedProductDataForSku = '';

      this.claimLine.issueTypeNameSelected = '';
      this.$forceUpdate();

      if (this.claimLine && this.claimLine.sku) {
        this.confirmLoading = true;

        axios
          .get(`/claims/sku/${this.claimLine.sku}/`)
          .then((response) => {
            if (!isEmpty(response.data.data)) {
              this.claimLine.modelName = response.data.data.modelName;
              this.claimLine.modelYear = response.data.data.modelYear;
              this.claimLine.size = response.data.data.size;
              this.claimLine.color = response.data.data.color;
              this.loadedProductDataForSku = this.claimLine.sku;

              this.$emit('issue-types-update', response.data.data.warrantyIssues);

              // Force update since we're modifying deeper attributes than what Vue is watching for
              this.$forceUpdate();
            }

            if (!isEmpty(response.data.meta.feedback.message)) {
              if (response.data.meta.feedback.message.basePropertyKey === 'warrantyClaims.B2B.unableLocateSku') {
                this.showSkuNotFound = true;
              }
            }
          })
          .finally(() => {
            this.confirmLoading = false;
            this.autoSaveClaim({csrf: this.csrf});
          });
      }
    },

    setImage({property, index = null}, {name, encodedFile}) {
      const image = {encodedFile, name, url: null};

      if (property === 'warrantyOptionalAttachments') {
        this.claimLine[property][index] = image;
      } else {
        this.claimLine[property] = image;
      }

      this.$nextTick(() => this.$validator.validate(this.inputName('serialImageValidator')));

      this.saveImageInput();
    },

    updateClaimLine() {
      this.autoSaveClaim({csrf: this.csrf});
    },

    updateVuex(e) {
      this.updateProperty({
        propName: e.target.getAttribute('name'),
        value: e.target.value,
      });
    },

    clearWarningMessages() {
      this.showSerialNotFound = false;
      this.showSkuNotFound = false;
    },

    updatePurchaseDate(e) {
      if (e && e.selectedDate instanceof Date) {
        const year = e.selectedDate.getFullYear();
        const month = ('0' + (e.selectedDate.getMonth() + 1)).slice(-2);
        const date = ('0' + e.selectedDate.getDate()).slice(-2);

        // i.e. 2019-05-23
        let formattedDate = `${year}-${month}-${date}`;

        if (this.claimLine.purchasedDate != formattedDate) {
          this.claimLine.purchasedDate = formattedDate;
          this.updateClaimLine(e);
        }
      }
    },

    clearImage(property) {
      this.claimLine[property] = {
        encodedImage: null,
        name: null,
        url: null,
      };

      this.$nextTick(() => this.$validator.validate(this.inputName('serialImageValidator')));
    },

    handlePartTypeChange(event) {
      this.claimLine.productType = event.target.value;
      this.clearWarningMessages();
      this.claimLine.issueTypeNameSelected = '';
      this.updateClaimLine();
    },

    ...mapActions('warrantyClaims', ['updateProperty', 'autoSaveClaim', 'saveClaim', 'saveImageInput']),
  },
};
</script>
<style scoped>
.warranty-claim-product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--gray-10);
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 0 25%;
  color: var(--gray-70);
}

.warranty-claim-product-overlay__label {
  display: flex;
}

.warranty-claim-product-overlay--loading {
  color: var(--gray-100);
  justify-content: center;
}
</style>
